import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Landmark, Hospital, GraduationCap, ShoppingBag, Factory, Plane, Building2, Zap, CheckCircle, ArrowRight, TrendingUp, Shield, Cloud } from 'lucide-react';
interface IndustriesProps {
  onBack?: () => void;
}
const industries = [{
  id: 1,
  icon: Landmark,
  name: 'Government & Public Sector',
  nameAr: 'الحكومة والقطاع العام',
  description: 'Digital transformation solutions for government entities',
  challenges: ['Legacy system modernization', 'Citizen service digitalization', 'Data security and privacy', 'Interagency collaboration'],
  solutions: ['Smart Government Platforms', 'e-Government Services', 'Digital Identity Management', 'Government Cloud Infrastructure'],
  benefits: ['Improved citizen satisfaction', 'Operational efficiency', 'Transparent governance', 'Cost reduction'],
  caseStudy: 'Transformed government ministry with 70% reduction in processing time',
  color: '#007A3D',
  stats: {
    projects: '45+',
    satisfaction: '95%',
    savings: '60%'
  }
}, {
  id: 2,
  icon: Hospital,
  name: 'Healthcare',
  nameAr: 'الرعاية الصحية',
  description: 'Secure and compliant IT solutions for healthcare providers',
  challenges: ['Patient data security', 'System integration', 'Regulatory compliance', 'Clinical workflow optimization'],
  solutions: ['Electronic Health Records (EHR)', 'Healthcare Cybersecurity', 'Telemedicine Platforms', 'Medical Device Integration'],
  benefits: ['Enhanced patient care', 'Secure data management', 'Regulatory compliance', 'Improved collaboration'],
  caseStudy: 'Major hospital achieved 100% HIPAA compliance with zero breaches',
  color: '#D7182A',
  stats: {
    projects: '30+',
    satisfaction: '98%',
    savings: '45%'
  }
}, {
  id: 3,
  icon: GraduationCap,
  name: 'Education',
  nameAr: 'التعليم',
  description: 'Modern technology infrastructure for educational institutions',
  challenges: ['Scalability for growth', 'Remote learning infrastructure', 'Student data management', 'Cost optimization'],
  solutions: ['Learning Management Systems', 'Cloud-Based Infrastructure', 'Virtual Classroom Solutions', 'Student Information Systems'],
  benefits: ['Enhanced learning experience', 'Flexible scalability', 'Cost efficiency', 'Better collaboration'],
  caseStudy: 'University scaled to 50,000 students with 90% uptime improvement',
  color: '#3498DB',
  stats: {
    projects: '25+',
    satisfaction: '93%',
    savings: '50%'
  }
}, {
  id: 4,
  icon: ShoppingBag,
  name: 'Retail & E-Commerce',
  nameAr: 'التجزئة والتجارة الإلكترونية',
  description: 'Digital commerce and customer experience solutions',
  challenges: ['Omnichannel integration', 'Payment security', 'Inventory management', 'Customer data analytics'],
  solutions: ['E-Commerce Platforms', 'Point of Sale Systems', 'Customer Analytics', 'Supply Chain Integration'],
  benefits: ['Increased sales', 'Better customer insights', 'Operational efficiency', 'Enhanced security'],
  caseStudy: 'Retail chain increased online sales by 200% in 6 months',
  color: '#9B59B6',
  stats: {
    projects: '35+',
    satisfaction: '96%',
    savings: '40%'
  }
}, {
  id: 5,
  icon: Factory,
  name: 'Manufacturing',
  nameAr: 'التصنيع',
  description: 'Industry 4.0 and smart manufacturing solutions',
  challenges: ['Production optimization', 'Supply chain visibility', 'Quality control', 'Predictive maintenance'],
  solutions: ['Industrial IoT Solutions', 'Manufacturing Execution Systems', 'Supply Chain Management', 'Predictive Analytics'],
  benefits: ['Increased productivity', 'Reduced downtime', 'Quality improvement', 'Cost savings'],
  caseStudy: 'Manufacturing plant achieved 35% productivity increase',
  color: '#E67E22',
  stats: {
    projects: '20+',
    satisfaction: '94%',
    savings: '55%'
  }
}, {
  id: 6,
  icon: Plane,
  name: 'Transportation & Logistics',
  nameAr: 'النقل والخدمات اللوجستية',
  description: 'Connected transportation and logistics technology',
  challenges: ['Fleet management', 'Route optimization', 'Real-time tracking', 'Compliance management'],
  solutions: ['Fleet Management Systems', 'GPS Tracking & Monitoring', 'Route Optimization Software', 'Warehouse Management Systems'],
  benefits: ['Optimized operations', 'Reduced fuel costs', 'Improved delivery times', 'Enhanced visibility'],
  caseStudy: 'Logistics company reduced delivery time by 40% with real-time tracking',
  color: '#1ABC9C',
  stats: {
    projects: '18+',
    satisfaction: '97%',
    savings: '48%'
  }
}];
export default function Industries({
  onBack
}: IndustriesProps) {
  const [selectedIndustry, setSelectedIndustry] = useState<typeof industries[0] | null>(null);
  return <div className="min-h-screen bg-gray-50">
      {/* Header with Back Button */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-[#007A3D] transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Home</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-[#007A3D] to-[#005B2A] text-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Industries We Serve
            </h1>
            <p className="text-xl text-white/90 mb-6">
              القطاعات التي نخدمها
            </p>
            <p className="text-lg text-white/80">
              Specialized IT solutions tailored to the unique challenges and requirements 
              of different industries across the GCC region.
            </p>
          </motion.div>
        </div>

        {/* Decorative Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48h1440V0c-240 48-480 48-720 24C480 0 240 0 0 24v24z" fill="#F9FAFB" />
          </svg>
        </div>
      </section>

      {/* Industry Cards Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {industries.map((industry, index) => <motion.div key={industry.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all cursor-pointer group" onClick={() => setSelectedIndustry(industry)}>
                <div className="h-2 w-full rounded-t-2xl" style={{
              backgroundColor: industry.color
            }} />
                
                <div className="p-6">
                  <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" style={{
                backgroundColor: `${industry.color}20`
              }}>
                    <industry.icon style={{
                  color: industry.color
                }} size={28} />
                  </div>

                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                    {industry.name}
                  </h3>
                  <p className="text-sm text-gray-500 mb-3">{industry.nameAr}</p>
                  <p className="text-gray-600 mb-4">{industry.description}</p>

                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-4 pt-4 border-t">
                    <div className="text-center">
                      <div className="text-2xl font-bold mb-1" style={{
                    color: industry.color
                  }}>
                        {industry.stats.projects}
                      </div>
                      <div className="text-xs text-gray-500">Projects</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold mb-1" style={{
                    color: industry.color
                  }}>
                        {industry.stats.satisfaction}
                      </div>
                      <div className="text-xs text-gray-500">Satisfaction</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold mb-1" style={{
                    color: industry.color
                  }}>
                        {industry.stats.savings}
                      </div>
                      <div className="text-xs text-gray-500">Cost Savings</div>
                    </div>
                  </div>

                  <button className="w-full py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors" style={{
                backgroundColor: `${industry.color}10`,
                color: industry.color
              }}>
                    Learn More
                    <ArrowRight size={18} />
                  </button>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Why Industry-Specific Solutions */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
              Why Industry-Specific Solutions Matter
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Understanding your industry's unique challenges and regulations
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[{
            icon: Shield,
            title: 'Compliance Expertise',
            description: 'Deep understanding of industry-specific regulations and standards'
          }, {
            icon: TrendingUp,
            title: 'Proven Track Record',
            description: 'Successful implementations across various industry sectors'
          }, {
            icon: Cloud,
            title: 'Tailored Technology',
            description: 'Solutions designed for your industry\'s specific workflows'
          }].map((feature, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-gray-50 p-8 rounded-2xl text-center">
                <div className="w-16 h-16 bg-[#007A3D]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="text-[#007A3D]" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-[#007A3D] to-[#005B2A] text-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Let's Discuss Your Industry Needs
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Connect with our industry experts to explore tailored solutions for your sector
            </p>
            <button className="bg-[#D7182A] hover:bg-[#b51525] text-white px-8 py-4 rounded-full font-semibold transition-colors inline-flex items-center gap-2" onClick={onBack}>
              Schedule Industry Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Industry Detail Modal */}
      {selectedIndustry && <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedIndustry(null)}>
          <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="h-3 w-full rounded-t-2xl" style={{
          backgroundColor: selectedIndustry.color
        }} />
            
            <div className="p-8">
              <button onClick={() => setSelectedIndustry(null)} className="text-gray-400 hover:text-gray-600 mb-4 float-right">
                ✕ Close
              </button>

              <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{
            backgroundColor: `${selectedIndustry.color}20`
          }}>
                <selectedIndustry.icon style={{
              color: selectedIndustry.color
            }} size={36} />
              </div>

              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {selectedIndustry.name}
              </h2>
              <p className="text-lg text-gray-500 mb-6">{selectedIndustry.nameAr}</p>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Common Challenges</h3>
                  <div className="space-y-2">
                    {selectedIndustry.challenges.map((challenge, i) => <div key={i} className="flex items-start gap-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{
                    backgroundColor: `${selectedIndustry.color}20`
                  }}>
                          <span style={{
                      color: selectedIndustry.color
                    }} className="text-xs font-bold">
                            {i + 1}
                          </span>
                        </div>
                        <span className="text-gray-700">{challenge}</span>
                      </div>)}
                  </div>
                </div>

                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Our Solutions</h3>
                  <div className="space-y-2">
                    {selectedIndustry.solutions.map((solution, i) => <div key={i} className="flex items-start gap-2">
                        <CheckCircle size={20} style={{
                    color: selectedIndustry.color
                  }} className="flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{solution}</span>
                      </div>)}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-xl mb-6">
                <h3 className="text-lg font-bold text-gray-800 mb-3">Key Benefits</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  {selectedIndustry.benefits.map((benefit, i) => <div key={i} className="flex items-center gap-2">
                      <TrendingUp size={16} style={{
                  color: selectedIndustry.color
                }} />
                      <span className="text-gray-700">{benefit}</span>
                    </div>)}
                </div>
              </div>

              <div className="p-6 rounded-xl mb-6 text-white" style={{
            backgroundColor: selectedIndustry.color
          }}>
                <h3 className="text-lg font-bold mb-2">Success Story</h3>
                <p className="text-white/90">{selectedIndustry.caseStudy}</p>
              </div>

              <button className="w-full py-4 rounded-full font-semibold text-white transition-colors flex items-center justify-center gap-2" style={{
            backgroundColor: selectedIndustry.color
          }} onClick={onBack}>
                Request Industry-Specific Consultation
                <ArrowRight size={20} />
              </button>
            </div>
          </motion.div>
        </div>}
    </div>;
}